import { useEffect, useRef } from "react";

const WIDGET_SCRIPT = "/booking-widget/booking-inline-test.js";
const WIDGET_CSS = "/booking-widget/booking-inline-test.css";

export default function BookingWidget({ inline = false }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;

    if (inline && !container) return;

    window.CABCITY_AUTO_INIT = false;

    const loadCss = () => {
      if (!document.querySelector(`link[href="${WIDGET_CSS}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = WIDGET_CSS;
        link.dataset.cabcityWidget = "true";
        document.head.appendChild(link);
      }
    };

    const loadScript = () => {
      loadCss();

      if (window.CabCityBooking) {
        return Promise.resolve(window.CabCityBooking);
      }

      const existingScript = document.querySelector(
        `script[src="${WIDGET_SCRIPT}"]`
      );

      if (existingScript) {
        return new Promise((resolve, reject) => {
          const check = () => {
            if (window.CabCityBooking) {
              resolve(window.CabCityBooking);
              return;
            }
            window.setTimeout(check, 25);
          };

          check();

          existingScript.addEventListener(
            "error",
            () => reject(new Error("CAB CITY booking widget failed to load.")),
            { once: true }
          );
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");

        script.src = WIDGET_SCRIPT;
        script.async = true;
        script.dataset.cabcityWidget = "true";

        script.onload = () => {
          if (window.CabCityBooking) {
            resolve(window.CabCityBooking);
          } else {
            reject(
              new Error("CAB CITY booking widget initialized incorrectly.")
            );
          }
        };

        script.onerror = () => {
          reject(new Error("CAB CITY booking widget failed to load."));
        };

        document.body.appendChild(script);
      });
    };

    let stopped = false;

    if (inline) {
      loadScript()
        .then((booking) => {
          if (stopped) return;

          const target = containerRef.current;
          if (!target) return;

          booking.init({
            mode: "inline",
            container: target,
          });
        })
        .catch((error) => {
          if (!stopped) console.error(error);
        });
    } else {
      const handleBookingClick = (event) => {
        const trigger = event.target.closest("[data-cabcity-booking]");
        if (!trigger) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        loadScript()
          .then((booking) => {
            if (!stopped && typeof booking.open === "function") {
              booking.open();
            }
          })
          .catch((error) => {
            if (!stopped) console.error(error);
          });
      };

      document.addEventListener("click", handleBookingClick);

      return () => {
        stopped = true;
        document.removeEventListener("click", handleBookingClick);
      };
    }

    return () => {
      stopped = true;

      if (inline && container) {
        container.innerHTML = "";
      }
    };
  }, [inline]);

  if (!inline) return null;

  return (
    <div
      ref={containerRef}
      id="cabcity-booking"
      className="w-full min-h-[870px]"
      data-testid="cabcity-booking-container"
    />
  );
}
